import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Email as EmailIcon,
  Info as InfoIcon,
} from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '@hooks/redux'
import {
  fetchAlertRules,
  deleteAlertRule,
  updateAlertRule,
} from '@store/slices/alertRulesSlice'
import { AlertRule } from '@types'

/**
 * AlertRules Page Component
 * 
 * Displays and manages alert rules for monitoring hive conditions.
 * Features:
 * - List all alert rules with active/inactive status
 * - Toggle alert rules on/off
 * - Enable/disable email notifications
 * - Edit and delete alert rules
 * - Create new custom alert rules
 * 
 * @component
 */
const AlertRules: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { alertRules, loadingState, error } = useAppSelector((state) => state.alertRules)

  const [showExplanation, setShowExplanation] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [loadingRuleIds, setLoadingRuleIds] = useState<{
    active: number[]
    email: number[]
  }>({ active: [], email: [] })

  useEffect(() => {
    dispatch(fetchAlertRules())
  }, [dispatch])

  useEffect(() => {
    if (alertRules.length === 0 && loadingState === 'succeeded') {
      setShowExplanation(true)
    }
  }, [alertRules, loadingState])

  const alertsEnabled = alertRules.some((rule) => rule.is_active)

  const handleToggleAllAlerts = async () => {
    const newState = !alertsEnabled
    for (const rule of alertRules) {
      if (rule.is_active !== newState) {
        await dispatch(updateAlertRule({ id: rule.id, data: { is_active: newState } }))
      }
    }
  }

  const handleToggleActive = async (rule: AlertRule) => {
    setLoadingRuleIds((prev) => ({ ...prev, active: [...prev.active, rule.id] }))
    await dispatch(updateAlertRule({ id: rule.id, data: { is_active: !rule.is_active } }))
    setLoadingRuleIds((prev) => ({
      ...prev,
      active: prev.active.filter((id) => id !== rule.id),
    }))
  }

  const handleToggleEmail = async (rule: AlertRule) => {
    setLoadingRuleIds((prev) => ({ ...prev, email: [...prev.email, rule.id] }))
    await dispatch(updateAlertRule({ id: rule.id, data: { alert_via_email: !rule.alert_via_email } }))
    setLoadingRuleIds((prev) => ({
      ...prev,
      email: prev.email.filter((id) => id !== rule.id),
    }))
  }

  const handleEdit = (id: number) => {
    navigate(`/alertrules/${id}/edit`)
  }

  const handleDelete = async (rule: AlertRule) => {
    if (window.confirm(`Are you sure you want to delete alert rule "${rule.name}"?`)) {
      await dispatch(deleteAlertRule(rule.id))
    }
  }

  const handleCreateNew = () => {
    navigate('/alertrules/create')
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const formatCalculationTime = (minutes: number) => {
    if (minutes === 0) return 'Instant'
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
  }

  if (loadingState === 'pending') {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    )
  }

  return (
    <Container sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        mb: 3,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant={isMobile ? "h5" : "h4"} component="h1">
            Alert Rules
          </Typography>
          <Tooltip title="Show explanation">
            <IconButton
              size="small"
              onClick={() => setShowExplanation(!showExplanation)}
              color={showExplanation ? 'primary' : 'default'}
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleMenuClick}
          fullWidth={isMobile}
          size={isMobile ? "large" : "medium"}
        >
          New Alert Rule
        </Button>
      </Box>

      {/* Explanation */}
      {showExplanation && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={() => setShowExplanation(false)}>
          <Typography variant="body2">
            Alert rules monitor your hive data and notify you when specific conditions are met.
            You can set thresholds for temperature, weight, humidity, and other measurements.
            Alerts can be sent via email to help you respond quickly to potential issues.
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Controls */}
      {alertRules.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={alertsEnabled}
                onChange={handleToggleAllAlerts}
                color="primary"
              />
            }
            label={alertsEnabled ? 'Alerts Enabled' : 'Alerts Disabled'}
          />
        </Box>
      )}

      {/* Alert Rules Table */}
      {alertRules.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <img
            src="/img/smartnyuki-alert-rule.png"
            alt="Alert Rule"
            style={{ maxWidth: '180px', marginBottom: theme.spacing(3) }}
          />
          <Typography variant="h6" gutterBottom>
            No Alert Rules
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Create alert rules to monitor your hives and receive notifications when conditions change.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Create First Alert Rule
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: isMobile ? 60 : 80 }}>Active</TableCell>
                <TableCell sx={{ minWidth: isMobile ? 60 : 120 }}>{isMobile ? 'Email' : 'Alert via Email'}</TableCell>
                <TableCell sx={{ minWidth: isMobile ? 120 : 200 }}>Name</TableCell>
                {!isMobile && <TableCell sx={{ minWidth: 120 }}>Sensor</TableCell>}
                {!isMobile && <TableCell sx={{ minWidth: 110 }}>Threshold</TableCell>}
                {!isMobile && <TableCell sx={{ minWidth: 90 }}>Severity</TableCell>}
                {!isMobile && <TableCell sx={{ minWidth: 120 }}>Window</TableCell>}
                <TableCell align="right" sx={{ minWidth: isMobile ? 80 : 120 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alertRules
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((rule) => (
                  <TableRow
                    key={rule.id}
                    sx={{ opacity: rule.is_active ? 1 : 0.5 }}
                  >
                    <TableCell>
                      {loadingRuleIds.active.includes(rule.id) ? (
                        <CircularProgress size={20} />
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleToggleActive(rule)}
                          color={rule.is_active ? 'success' : 'error'}
                        >
                          {rule.is_active ? <ActiveIcon /> : <InactiveIcon />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      {loadingRuleIds.email.includes(rule.id) ? (
                        <CircularProgress size={20} />
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleToggleEmail(rule)}
                          color={rule.alert_via_email ? 'success' : 'error'}
                        >
                          {rule.alert_via_email ? <ActiveIcon /> : <InactiveIcon />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{rule.name}</Typography>
                      {rule.description && (
                        <Typography variant="caption" color="textSecondary">
                          {rule.description}
                        </Typography>
                      )}
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        {rule.measurement_type_name || 'Any sensor'}
                      </TableCell>
                    )}
                    {!isMobile && (
                      <TableCell>
                        {rule.comparison_operator || rule.comparison || ''} {rule.threshold_value ?? '—'}
                      </TableCell>
                    )}
                    {!isMobile && (
                      <TableCell>
                        <Chip
                          label={rule.severity || 'medium'}
                          size="small"
                          color={
                            rule.severity === 'critical' || rule.severity === 'high'
                              ? 'error'
                              : rule.severity === 'low'
                                ? 'default'
                                : 'warning'
                          }
                          variant="outlined"
                        />
                      </TableCell>
                    )}
                    {!isMobile && (
                      <TableCell>
                        <Chip
                          label={formatCalculationTime(rule.calculation_minutes || 0)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(rule.id)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(rule)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose()
            handleCreateNew()
          }}
        >
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>New Alert Rule</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose()
            navigate('/alertrules/default')
          }}
        >
          <ListItemIcon>
            <CopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Select Default Alert Rule</ListItemText>
        </MenuItem>
      </Menu>
    </Container>
  )
}

export default AlertRules
